import 'cross-fetch/polyfill'
import camelcaseKeys from 'camelcase-keys'
import type { RequestInit, RequestCredentials } from 'node-fetch'

// Define HTTP method types
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

// Type for API query parameters
export type ApiParams = Record<string, string | number | boolean | string[] | undefined | null>

// Define request options interface
export interface RequestOptions {
  headers?: Record<string, string>
  params?: ApiParams
  data?: unknown
  cache?: 'default' | 'no-store' | 'reload' | 'no-cache' | 'force-cache' | 'only-if-cached'
  credentials?: 'include' | 'same-origin' | 'omit'
  timeout?: number
  responseType?: 'json' | 'text' | 'blob' | 'arrayBuffer'
  signal?: AbortSignal
  skipRequestCancellation?: boolean
}

// Define API response interface
export interface ApiResponse<T = any> {
  data: T
  status: number
  statusText: string
  headers: Record<string, string>
}

// サーバー側のデフォルトタイムアウト。Rails応答が遅延した際にin-flightの
// フェッチとRSCレンダーコンテキストが滞留し続けるのを防ぐ（undiciのデフォルトは最大300秒）
const DEFAULT_SERVER_TIMEOUT_MS = 10000

// Error class for API calls
export class ApiError extends Error {
  status: number
  statusText: string
  data: any

  constructor(message: string, status: number, statusText: string, data?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText
    this.data = data
  }
}

/**
 * Main API client class to handle all API requests
 */
export class ApiClient {
  private baseUrl: string
  private defaultOptions: RequestOptions
  private pendingRequests: Map<string, AbortController>
  private isServer: boolean

  constructor(baseUrl?: string, defaultOptions?: RequestOptions) {
    this.isServer = typeof window === 'undefined'
    this.baseUrl = baseUrl || this.getBaseUrl()
    this.pendingRequests = new Map()

    // サーバーサイドの場合、環境変数の存在を確認
    if (this.isServer) {
      this.validateServerEnvVars()
    }

    const authHeader = this.getAuthHeader()
    const authToken = this.getAuthToken()

    this.defaultOptions = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: authHeader,
        'Auth-Token-Api': authToken,
      },
      cache: 'no-store',
      credentials: 'include',
      responseType: 'json',
      ...(this.isServer ? { timeout: DEFAULT_SERVER_TIMEOUT_MS } : {}),
      ...defaultOptions,
    }
  }

  /**
   * Validate server-side environment variables
   */
  private validateServerEnvVars(): void {
    // Skip validation during build/prerendering to prevent build failures
    // This includes Docker builds where env vars may not be available
    if (
      process.env.NODE_ENV === 'production' ||
      process.env.NEXT_PHASE === 'phase-production-build' ||
      !process.env.BASIC_AUTH_USER ||
      !process.env.BASIC_AUTH_PASSWORD
    ) {
      return
    }

    // Basic認証は本番以外でのみ必須
    const requiredVars: Record<string, string | undefined> = {
      API_URL: process.env.API_URL,
      AUTH_TOKEN_API_HEADERS: process.env.AUTH_TOKEN_API_HEADERS,
    }

    // 本番以外の場合はBasic認証も必須
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
      requiredVars.BASIC_AUTH_USER = process.env.BASIC_AUTH_USER
      requiredVars.BASIC_AUTH_PASSWORD = process.env.BASIC_AUTH_PASSWORD
    }

    const missingVars = Object.entries(requiredVars)
      .filter(([, value]) => !value)
      .map(([key]) => key)

    if (missingVars.length > 0) {
      throw new Error(`Missing required server environment variables: ${missingVars.join(', ')}`)
    }
  }

  /**
   * Get base URL based on environment
   */
  private getBaseUrl(): string {
    const url = this.isServer ? process.env.API_URL : process.env.NEXT_PUBLIC_API_URL

    if (!url) {
      // During build/prerendering, provide fallback URL to prevent build failures
      if (this.isServer && process.env.NODE_ENV === 'production') {
        return 'https://staging-api.talent-book.jp/api' // Fallback for build
      }

      throw new Error('API URL is not configured')
    }

    return url
  }

  /**
   * Get auth token based on environment
   */
  private getAuthToken(): string {
    const token = this.isServer
      ? process.env.AUTH_TOKEN_API_HEADERS
      : process.env.NEXT_PUBLIC_AUTH_TOKEN_API_HEADERS

    return token || ''
  }

  /**
   * Get auth header for basic authentication
   */
  private getAuthHeader(): string {
    const username = this.isServer
      ? process.env.BASIC_AUTH_USER
      : process.env.NEXT_PUBLIC_BASIC_AUTH_USER
    const password = this.isServer
      ? process.env.BASIC_AUTH_PASSWORD
      : process.env.NEXT_PUBLIC_BASIC_AUTH_PASSWORD

    if (!username || !password) {
      return ''
    }

    return `Basic ${btoa(`${username}:${password}`)}`
  }

  /**
   * Format URL with query parameters
   */
  private formatUrl(path: string, params?: ApiParams): string {
    // baseURLがすでに/apiで終わっている場合は、追加の/api/を付けない
    let apiPath: string
    apiPath = path.startsWith('/api/') ? path : `/api/${path}`
    const url = new URL(apiPath, this.baseUrl)
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            // If the key already ends with [], don't add brackets again
            const paramKey = key.endsWith('[]') ? key : `${key}[]`
            value.forEach((v) => url.searchParams.append(paramKey, String(v)))
          } else {
            url.searchParams.append(key, String(value))
          }
        }
      })
    }
    return url.toString()
  }

  /**
   * Generate a unique key for the request
   */
  private getRequestKey(method: HttpMethod, path: string, params?: ApiParams): string {
    const url = this.formatUrl(path, params)
    return `${method}:${url}`
  }

  /**
   * Cancel a specific request by its key
   */
  private cancelRequestByKey(key: string, reason = 'Request cancelled') {
    const controller = this.pendingRequests.get(key)
    if (controller) {
      controller.abort(reason)
      this.pendingRequests.delete(key)
    }
  }

  /**
   * Process response based on response type
   */
  private async processResponse(response: Response, responseType?: string): Promise<any> {
    if (!responseType || responseType === 'json') {
      // Check if response has content before trying to parse JSON
      const contentLength = response.headers.get('content-length')

      // If no content or content-length is 0 or status 204, return null
      if (contentLength === '0' || response.status === 204) {
        return null
      }

      // Read response as text first to check if it's empty
      const text = await response.text()
      if (!text || text.trim() === '') {
        return null
      }

      // Parse JSON
      let data
      try {
        data = JSON.parse(text)
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e)
        throw new Error(`Invalid JSON response: ${errorMessage}`)
      }

      try {
        return camelcaseKeys(data, { deep: true })
      } catch (camelcaseError) {
        return data
      }
    }

    switch (responseType) {
      case 'text':
        return response.text()
      case 'blob':
        return response.blob()
      case 'arrayBuffer':
        return response.arrayBuffer()
      default:
        return response.json()
    }
  }

  /**
   * Get current headers for debugging
   */
  public getCurrentHeaders(): Record<string, string> {
    return {
      ...this.defaultOptions.headers,
      Authorization: this.defaultOptions.headers?.Authorization ? '[REDACTED]' : 'undefined',
      'Auth-Token-Api': this.defaultOptions.headers?.['Auth-Token-Api']
        ? '[REDACTED]'
        : 'undefined',
    }
  }

  /**
   * Reinitialize headers (useful after login/logout)
   */
  public reinitializeHeaders(): void {
    const authHeader = this.getAuthHeader()
    const authToken = this.getAuthToken()

    this.defaultOptions.headers = {
      ...this.defaultOptions.headers,
      Authorization: authHeader,
      'Auth-Token-Api': authToken,
    }
  }

  /**
   * Main request method to handle all API calls
   */
  public async request<T = any>(
    method: HttpMethod,
    path: string,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    const requestKey = this.getRequestKey(method, path, options?.params)
    const shouldSkipCancellation = options?.skipRequestCancellation || false
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    let localController: AbortController | undefined

    // 外部から提供されたsignalがない場合は、新しいAbortControllerを作成
    if (!options?.signal) {
      // 同じリクエストが進行中の場合は、キャンセルするかどうかを判断
      if (this.pendingRequests.has(requestKey) && !shouldSkipCancellation) {
        // skipRequestCancellationがfalseの場合のみ、既存のリクエストをキャンセル
        this.cancelRequestByKey(requestKey, 'New request initiated')
      }

      // skipRequestCancellationが有効な場合は、pendingRequestsに登録せず独立したAbortControllerを使用
      const controller = new AbortController()
      localController = controller
      if (!shouldSkipCancellation) {
        this.pendingRequests.set(requestKey, controller)
      }
      options = {
        ...options,
        signal: controller.signal,
      }
    }

    const mergedOptions: RequestOptions = {
      ...this.defaultOptions,
      ...options,
      headers: {
        ...this.defaultOptions.headers,
        ...options?.headers,
      },
    }

    const url = this.formatUrl(path, mergedOptions.params)

    try {
      // タイムアウト時はこのリクエスト自身のcontrollerを直接abortする。
      // pendingRequests経由（cancelRequestByKey）だとskipRequestCancellation時は未登録のため
      // abortされず、タイムアウトが実質無効になる。外部signal提供時はabort手段がないため何もしない
      // （pendingRequests経由でabortすると同一キーの別リクエストを誤って中断してしまう）。
      if (mergedOptions.timeout && localController) {
        const controller = localController
        timeoutId = setTimeout(() => {
          controller.abort(`Request timeout after ${mergedOptions.timeout}ms`)
          if (!shouldSkipCancellation) {
            this.pendingRequests.delete(requestKey)
          }
        }, mergedOptions.timeout)
      }

      const fetchOptions: RequestInit = {
        method,
        headers: mergedOptions.headers,
        credentials: mergedOptions.credentials as RequestCredentials,
        signal: mergedOptions.signal,
        cache: mergedOptions.cache,
      }

      if (method !== 'GET' && mergedOptions.data) {
        fetchOptions.body = JSON.stringify(mergedOptions.data)
      }

      const response = await fetch(url, fetchOptions)

      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      if (!response.ok) {
        let errorData: any
        try {
          errorData = await response.json()
        } catch (e) {
          errorData = { message: response.statusText }
        }

        // エラーレスポンスをログ出力
        console.error('[apiClient] Error Response:', {
          status: response.status,
          statusText: response.statusText,
          url,
          errorData,
        })

        const errorMessage =
          errorData.message ||
          (response.status === 500
            ? 'システムに予期せぬエラーが発生しました。ご不便をおかけしますが、復旧までしばらくお待ちください。'
            : `Request failed with status ${response.status}`)

        throw new ApiError(errorMessage, response.status, response.statusText, errorData)
      }

      const data = await this.processResponse(response, mergedOptions.responseType)

      return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }

      // Check for request cancellation first (including string error messages from AbortController)
      const errorString =
        typeof error === 'string' ? error : error instanceof Error ? error.message : ''
      const isCancellation =
        (error instanceof DOMException && error.name === 'AbortError') ||
        (error instanceof Error && error.name === 'AbortError') ||
        errorString === 'New request initiated' ||
        errorString.includes('Request cancelled') ||
        errorString.includes('Request timeout')

      if (isCancellation) {
        // Don't log cancelled requests as errors - they are expected behavior
        throw new ApiError('Request cancelled', 0, 'Cancelled')
      }

      // ネットワークエラーやタイムアウトの場合
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new ApiError(
          'サーバーに接続できません。ネットワーク接続を確認してください。',
          0,
          'Network Error',
        )
      }

      // Check for specific error types
      let errorMessage = 'An unknown error occurred'
      let errorStatus = 500
      let errorDetails = error

      if (error instanceof TypeError) {
        if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Network error: Unable to connect to API server'
          errorStatus = 0
        } else if (error.message.includes('NetworkError')) {
          errorMessage = 'Network error: Connection failed'
          errorStatus = 0
        } else if (error.message.includes('CORS')) {
          errorMessage = 'CORS error: Cross-origin request blocked'
          errorStatus = 0
        } else {
          errorMessage = `TypeError: ${error.message}`
        }
      } else if (error instanceof Error) {
        errorMessage = error.message || errorMessage
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      // Include more details in development
      if (process.env.NODE_ENV !== 'production') {
        console.error('[ApiClient] Request error details:', {
          url,
          method,
          error,
          errorMessage,
          errorStatus,
        })
      }

      throw new ApiError(errorMessage, errorStatus, 'Internal Error', errorDetails)
    } finally {
      // メモリリーク修正: finally ブロックで必ずクリーンアップを実行
      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      // リクエストが完了したら、pendingRequestsから削除
      // skipRequestCancellationの場合は登録していないので削除不要
      if (!shouldSkipCancellation) {
        this.pendingRequests.delete(requestKey)
      }
    }
  }

  /**
   * GET request
   */
  public async get<T = any>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path, options)
  }

  /**
   * POST request
   */
  public async post<T = any>(
    path: string,
    data?: any,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, { ...options, data })
  }

  /**
   * PUT request
   */
  public async put<T = any>(
    path: string,
    data?: any,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, { ...options, data })
  }

  /**
   * PATCH request
   */
  public async patch<T = any>(
    path: string,
    data?: any,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', path, { ...options, data })
  }

  /**
   * DELETE request
   */
  public async delete<T = any>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path, options)
  }
}

// Create and export a default instance
const apiClient = new ApiClient()

// Expose apiClient globally for debugging and reinitialization
if (typeof window !== 'undefined') {
  ;(window as any).apiClient = apiClient
}

export default apiClient
